import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, Lock, UserPlus } from "lucide-react";
import { lookbookApi } from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { getErrorMessage } from "@/lib/api-error";
import {
  isLookbookPasswordRequired,
  setLookbookToken,
} from "@/lib/lookbook-access";
import { CoverGrid, PageContainer, PoweredBy, RaiseBoardShell } from "./rb-ui";

/**
 * Stands in front of every /lb/ page. When the lookbook has a password and this
 * browser hasn't unlocked it, the visitor gets a password prompt instead of the
 * page; otherwise the page renders untouched.
 *
 * It runs the same ["lookbook", id] query the pages themselves read, so a
 * lookbook with no password costs no extra request — the pages pick the
 * payload up from the cache. Every other failure is left to the page, which
 * already knows how to say "this lookbook isn't available".
 */
export function LookbookPasswordGate({ children }: { children: ReactNode }) {
  const { lookbookPublicId } = useParams<{ lookbookPublicId: string }>();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const { error, isPending } = useQuery({
    queryKey: ["lookbook", lookbookPublicId],
    queryFn: () => lookbookApi.getByPublicId(lookbookPublicId as string),
    enabled: !!lookbookPublicId,
    retry: false,
  });

  const locked = !!lookbookPublicId && isLookbookPasswordRequired(error);

  // A stored token the server no longer accepts — the owner changed the
  // password — is dead weight; drop it so the next request doesn't send it.
  useEffect(() => {
    if (locked && lookbookPublicId) setLookbookToken(lookbookPublicId, null);
  }, [locked, lookbookPublicId]);

  // Until the first answer is in, we don't know whether the lookbook is locked,
  // so the page is held back — rendering it would flash a protected lookbook's
  // name and layout before the password prompt replaces it.
  if (lookbookPublicId && isPending) {
    return (
      <RaiseBoardShell footer={false}>
        {/* The shape of a lookbook's opening, so whichever way the answer
            comes back the page settles rather than jumps. */}
        <div role="status" aria-label="Loading lookbook" className="ink-scope bg-background">
          <div className="h-14 border-b border-border/60" />
          <PageContainer className="pb-24 pt-16 sm:pt-24">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="mt-8 h-20 w-full max-w-xl rounded-xl" />
            <Skeleton className="mt-12 h-4 w-80" />
          </PageContainer>
        </div>
      </RaiseBoardShell>
    );
  }

  if (!locked) return <>{children}</>;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || !lookbookPublicId) return;
    setSubmitting(true);
    setProblem(null);
    try {
      const token = await lookbookApi.unlock(lookbookPublicId, password);
      setLookbookToken(lookbookPublicId, token);
      // Everything cached under this lookbook was fetched without the token —
      // the board, comments, votes — so all of it is refetched, not just the
      // payload this gate watches.
      await queryClient.resetQueries({ queryKey: ["lookbook", lookbookPublicId] });
      queryClient.invalidateQueries();
    } catch (err) {
      setProblem(getErrorMessage(err, "Couldn't check that password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RaiseBoardShell footer={false}>
      {/* The deal room before you're let in: flat ink, the page grid drawn in
          hairlines, a little grain. */}
      <main className="ink-scope grain relative isolate flex min-h-dvh flex-col bg-background px-5 [--grain-opacity:0.09]">
        <CoverGrid width="narrow" />
        <div className="flex flex-1 items-center justify-center py-16">
          <div className="w-full max-w-[380px] motion-safe:animate-rise">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-card shadow-[0_0_0_6px_hsl(var(--card)/0.5)]">
              <Lock className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>

            <p className="mt-9 text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              By invitation
            </p>
            <h1 className="mt-3 text-[clamp(2.25rem,1.8rem+1.8vw,3rem)] font-semibold leading-[1.02] tracking-[-0.04em]">
              This lookbook is private
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
              Enter the password you were given to open it.
            </p>

            <form onSubmit={submit} className="mt-8">
              <Input
                type="password"
                autoFocus
                autoComplete="current-password"
                aria-label="Lookbook password"
                aria-invalid={!!problem}
                aria-describedby={problem ? "lookbook-password-problem" : undefined}
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setProblem(null);
                }}
                className="h-11 bg-card px-3.5 text-[15px] aria-[invalid=true]:border-destructive/60"
              />
              {problem && (
                <p
                  id="lookbook-password-problem"
                  role="alert"
                  className="mt-2 text-sm text-destructive"
                >
                  {problem}
                </p>
              )}
              <Button
                type="submit"
                className="mt-3 h-11 w-full gap-2 text-[15px]"
                disabled={!password || submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Open lookbook
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>

            {lookbookPublicId && <RequestAccess publicId={lookbookPublicId} />}
          </div>
        </div>

        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </main>
    </RaiseBoardShell>
  );
}

/**
 * For someone who reached the lookbook without the password, usually because
 * the link went to the wrong address. Opens inline under the password form
 * and sends the fund a note; the fund decides whether to invite them.
 */
function RequestAccess({ publicId }: { publicId: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (sent) {
    return (
      <p
        role="status"
        className="mt-10 border-t border-border pt-6 text-[14px] leading-relaxed text-muted-foreground"
      >
        <span className="font-medium text-foreground">Request sent.</span> The
        fund will get your note and can invite you directly.
      </p>
    );
  }

  if (!open) {
    return (
      <div className="mt-10 border-t border-border pt-6">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Don&rsquo;t have the password, or think this reached the wrong person?
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          className="mt-3 h-10 gap-2 bg-card px-4 text-[14px]"
        >
          <UserPlus className="h-4 w-4" />
          Request access
        </Button>
      </div>
    );
  }

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    setProblem(null);
    try {
      await lookbookApi.requestAccess(publicId, {
        email: email.trim(),
        name: name.trim() || undefined,
        message: note.trim() || undefined,
      });
      setSent(true);
    } catch (err) {
      setProblem(getErrorMessage(err, "Couldn't send your request"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={send}
      aria-label="Request access"
      className="mt-10 space-y-3 border-t border-border pt-6 motion-safe:animate-rise"
    >
      <p className="text-[14px] font-medium">Request access</p>
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        The fund that shared this lookbook will get your details and can invite
        you. Your note goes to them, nobody else.
      </p>
      <Input
        type="email"
        required
        autoFocus
        autoComplete="email"
        aria-label="Your email"
        placeholder="you@fund.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setProblem(null);
        }}
        className="h-10 bg-card px-3.5 text-[14px]"
      />
      <Input
        autoComplete="name"
        aria-label="Your name"
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="h-10 bg-card px-3.5 text-[14px]"
      />
      <Textarea
        aria-label="Note to the fund"
        placeholder="A short note (optional), e.g. who sent you the link"
        maxLength={500}
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="bg-card px-3.5 text-[14px]"
      />
      {problem && (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" className="h-10 px-4 text-[14px]" disabled={!email || sending}>
          {sending && <Loader2 className="h-4 w-4 animate-spin" />}
          Send request
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-10 px-3 text-[14px] text-muted-foreground"
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
